import { merge } from 'lodash';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { yellow } from '@material-ui/core/colors';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import {
  DetailContext,
  DetailToolbarBtnName,
  EmptyComponent,
  EvidenceProps,
  abortableFetch,
  updateItemFactory,
} from '@eas/common-web';
import { PairedLockEditButton } from '@composite/detail/paired-lock-edit-button';
import { useAuthoredEvidence } from '@composite/evidence/authored-evidence';
import { CourtFile, Decision } from '@models';
import {
  Agenda,
  DecisionState,
  EntityName,
  EvidenceApiUrl,
  ExportTags,
  UIElement,
} from '@enums';
import { ActionBar } from './decision-actionbar';
import { useColumns } from './decision-columns';
import { Fields } from './decision-fields';
import { useDecisionsHookedSources } from './decision-hooked-sources';
import { useDecisionPermission } from './decision-permission';
import { DecisionTableHeader } from './decision-table-header';
import { Toolbar } from './decision-toolbar';

const createRowStyle = (item: Decision): CSSProperties | undefined => {
  const style: CSSProperties = {};

  if ((item?.esFile as unknown as CourtFile)?.anonymizationRequest) {
    style.backgroundColor = yellow[100];
  }

  if (item.state === DecisionState.CANCELED) {
    style.color = '#777';
    style.textDecoration = 'line-through';
  }

  return Object.keys(style).length > 0 ? style : undefined;
};

function updateDecision(api: string, item: Decision) {
  const esFile = item.esFile as CourtFile | undefined;
  const isCourtFile = esFile?.agenda === Agenda.COURT;

  return abortableFetch(`${api}/${item.id}`, {
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    method: 'PUT',
    body: JSON.stringify({
      ...item,
      courtFileUpdate: isCourtFile
        ? {
            version: esFile.version,
            processingType: esFile.processingType,
            settlementMethods: esFile.settlementMethods,
            proceedingsSubjects: esFile.proceedingsSubjects,
            subjectIndexes: esFile.subjectIndexes,
            analystUserRoleId: esFile.analystUserRole
              ? { id: esFile.analystUserRole.id }
              : undefined,
          }
        : undefined,
    }),
  });
}

export function useDecisions({
  evidenceProps,
}: {
  evidenceProps?: Partial<EvidenceProps<Decision>>;
}) {
  const intl = useIntl();
  const columns = useColumns();

  const props = merge(
    {
      version: 25,
      identifier: evidenceProps?.identifier ?? 'DECISIONS',
      apiProps: {
        url: EvidenceApiUrl.DECISIONS,
        updateItem: updateItemFactory({
          updateItemMethod: updateDecision,
        }),
        hookedSources: useDecisionsHookedSources,
      },
      tableProps: {
        columns: evidenceProps?.tableProps?.columns ?? columns,
        reportTag: ExportTags.DECISIONS,
        defaultSorts: [
          {
            datakey: undefined,
            field: '_score',
            order: 'DESC',
            type: 'SCORE',
          },
        ],
        HeaderComponent: DecisionTableHeader,
        enableSearchHighlighting: true,
        createRowStyle,
        searchHighlight: [
          'text.legalSentence',
          'text.abstract',
          'text.introduction',
          'text.statement',
          'text.narration',
          'text.argumentation',
          'text.differentOpinion',
          'anonymizedText.legalSentence',
          'anonymizedText.abstract',
          'anonymizedText.introduction',
          'anonymizedText.statement',
          'anonymizedText.narration',
          'anonymizedText.argumentation',
          'anonymizedText.differentOpinion',
        ],
      },
      detailProps: {
        entityName: EntityName.DECISION,
        GeneralFieldsComponent: EmptyComponent,
        FieldsComponent: Fields,
        toolbarProps: {
          EditButton: PairedLockEditButton,
          style: {
            background: 'linear-gradient(to bottom, #FAFAFA, #4CAF50)',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
            borderBottom: '3px solid #388E3C',
          },
          title: intl.formatMessage({
            id: 'ES__DECISIONS__DETAIL__TITLE',
            defaultMessage: 'Rozhodnutí',
          }),
          subtitle: (value: Decision) => value?.number ?? '',
          disableBtn: disableBtn(),
          showBtn: (btn: string) => !['NEW', 'REMOVE'].includes(btn),
          ActionBar,
          After: Toolbar,
        },
      },
    },
    evidenceProps
  );

  const evidenceBag = useAuthoredEvidence<Decision>(props);

  return { evidenceBag, props };
}

export function disableBtn() {
  return function useDisableBtn(button: DetailToolbarBtnName) {
    const { source } = useContext(DetailContext);

    const { canExecute } = useDecisionPermission(source.data);

    if (
      button === 'EDIT' &&
      (source.data?.state === DecisionState.CANCELED ||
        !canExecute(UIElement.Decision.EDIT_DECISION_BUTTON))
    ) {
      return true;
    }

    return false;
  };
}
